import { Grid, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import HandleOperation from 'components/utils/HandleOperation';
import { InsightType } from '../../apis';
import InsightSkeleton from './InsightSkeleton';
import InsightsSearch from './InsightsSearch';
import NoInsights from './NoInsights';
import RenderInsight from './RenderInsight';
import { useInsights } from '../../hooks';
import Filter from 'components/common/filter/Filter';
import { GetInsightsQueryFnType, GetInsightsQueryKeyType } from '../../queries/useInsightsQuery';
import {
  GetInsightsSummaryQueryFnType,
  GetInsightsSummaryQueryKeyType,
} from '../../queries/useInsightsSummaryQuery';

type RenderInsightsProps = {
  getInsightsQueryFn?: GetInsightsQueryFnType;
  getInsightsQueryKey?: GetInsightsQueryKeyType;
  getInsightsSummaryQueryFn?: GetInsightsSummaryQueryFnType;
  getInsightsSummaryQueryKey?: GetInsightsSummaryQueryKeyType;
};

function RenderInsights({
  getInsightsQueryFn,
  getInsightsQueryKey,
  getInsightsSummaryQueryFn,
  getInsightsSummaryQueryKey,
}: RenderInsightsProps) {
  const {
    filters,
    hasActiveFilters,
    includeBWInsights,
    insightTypeState,
    insightsModule,
    isError,
    isFiltersLoading,
    isLoading,
    isLoadingBuildWiseModules,
    isLoadingGroupByValues,
    mappedFilters,
    mappedInsights,
    menu,
    searchInputRef,
    searchKeyword,
    clearFilters,
    handleClearSearch,
    handleSearchKeyDown,
    setFilters,
    setMenu,
  } = useInsights({ getInsightsQueryFn, getInsightsQueryKey });

  return (
    <>
      <StickyContainer id="insights-container">
        <Grid container spacing={[0, 2]}>
          <Grid item md={8}>
            <InsightsSearch
              ref={searchInputRef}
              menu={menu}
              setMenu={setMenu}
              onSearchKeyDown={handleSearchKeyDown}
              onClearSearch={handleClearSearch}
              searchInputRefValue={searchInputRef.current}
            />
          </Grid>
        </Grid>
      </StickyContainer>
      <Grid container spacing={2}>
        <Grid item md={8}>
          <HandleOperation
            isLoading={isLoading || isLoadingBuildWiseModules || isLoadingGroupByValues}
            isError={isError}
            data={mappedInsights}
            skeltonCount={8}
            skelton={<InsightSkeleton />}
            noDataComponent={
              <NoInsights
                hasActiveFilters={!!(searchKeyword || hasActiveFilters)}
                clearFilters={() => {
                  clearFilters();
                  handleClearSearch();
                }}
              />
            }
          >
            {mappedInsights.map((insight) => (
              <RenderInsight
                key={insight.title}
                includeBWInsights={includeBWInsights}
                insight={insight}
                groupBy={menu?.selectedOption?.value as string}
                searchKeyword={searchKeyword}
                filters={mappedFilters}
                insightTypeState={insightTypeState}
                getInsightsSummaryQueryFn={getInsightsSummaryQueryFn}
                getInsightsSummaryQueryKey={getInsightsSummaryQueryKey}
              />
            ))}
          </HandleOperation>
        </Grid>
        {(insightsModule?.referenceObject?.filterBy?.isActive ||
          insightsModule?.referenceObject?.filterBy?.isActive === undefined) && (
          <Grid item md={4}>
            <Filter
              filters={filters}
              setFilters={setFilters}
              isLoading={isFiltersLoading || isLoadingBuildWiseModules}
              clearFilters={clearFilters}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
}

const StickyContainer = styled(Box, { label: 'StickyContainer' })(({ theme }) => ({
  position: 'sticky',
  top: 79,
  paddingBottom: 16,
  marginTop: 8,
  backgroundColor: theme.palette.custom.background,
  zIndex: 2,
  width: '100%',
}));

export default RenderInsights;
